import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenBackdrop } from './ScreenBackdrop';
import { PresaleConfirmSheet } from './PresaleConfirmSheet';
import { ChevronRight } from './icons';
import { IncptIcon } from '../icons/IncptIcon';
import { TransactionRow, incptLedgerToTx, presalePurchaseToTx } from './TransactionRow';
import { useBackButton, hasTelegramBackButton } from '../../hooks/useBackButton';
import { useHaptic } from '../../hooks/useHaptic';
import { usePresale } from '../../hooks/usePresale';
import { useIncptTransfers } from '../../hooks/useIncptTransfers';
import { discountPercent, formatPrice, tokensFor } from '../../lib/presale';
import {
  openPresaleLegal,
  PRESALE_AGREEMENT_URL,
  PRESALE_PRIVACY_URL,
  PRESALE_RISK_URL,
} from '../../lib/presaleLegal';
import { formatUsd } from '../../lib/format';
import { decimalIsPositive, formatDecimalDisplay } from '@incpt/shared';

/** The prototype's presale gradient, reused by the entry-point CTA. */
export const PRESALE_GRADIENT = 'linear-gradient(120deg,#54a8e6,#3fa8c6 52%,#57c89a)';
export const PRESALE_ACCENT = '#3fa8c6';

const fmtWhole = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;
const fmtTokens = (n: number) =>
  n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function compact(n: number) {
  if (n >= 1_000_000) return `$${+(n / 1_000_000).toFixed(n % 1_000_000 ? 1 : 0)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${n}`;
}

function amountFontSize(digits: number) {
  if (digits <= 7) return 56;
  if (digits <= 9) return 44;
  return 34;
}

export function PresaleScreen({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const { impact, selection } = useHaptic();
  const { round, purchases, balance, usdtBalance, loading } = usePresale();
  const { transfers: incptTransfers } = useIncptTransfers();
  useBackButton(onClose);
  const tgBack = hasTelegramBackButton();

  const [raw, setRaw] = useState('');
  const [confirming, setConfirming] = useState(false);

  const amount = Number(raw) || 0;
  const tokens = tokensFor(amount, round);
  const belowMin = amount > 0 && amount < round.minAmount;
  const insufficient = amount > usdtBalance;
  const canSubmit = round.open && amount >= round.minAmount && !insufficient && !!round.treasuryTrx;

  const quick = [1, 3, 5, 10].map((m) => round.minAmount * m);

  // Same mix as the INCPT coin page: P2P / grants / promocodes + purchases.
  const historyRows = useMemo(
    () =>
      [
        ...incptTransfers
          .filter((e) => e.kind !== 'purchase')
          .map((e) => incptLedgerToTx(e, i18n.language, t)),
        ...purchases.map((p) => presalePurchaseToTx(p, i18n.language, t)),
      ].sort((a, b) => (b.sortTs ?? 0) - (a.sortTs ?? 0)),
    [incptTransfers, purchases, i18n.language, t],
  );

  const hint = !round.open
    ? t('presale.roundClosed')
    : !round.treasuryTrx
      ? t('presale.errTreasury')
      : belowMin
        ? t('presale.hintBelowMin', { amount: fmtWhole(round.minAmount) })
        : insufficient
          ? t('presale.hintBalance')
          : null;

  return (
    <div className="relative min-h-full">
      <ScreenBackdrop height={420} fadeStart={34} />
      <div className="relative z-10 flex min-h-full flex-col px-4 pb-[calc(var(--tg-safe-bottom)_+_2.5rem)] pt-[calc(var(--tg-safe-top)_+_0.75rem)]">
        <header className="flex items-center gap-3 py-2">
          {!tgBack && (
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.06]"
              aria-label="Back"
            >
              <ChevronRight size={16} className="rotate-180 text-ink" />
            </button>
          )}
          <h1 className="text-[20px] font-medium text-ink">{t('presale.title')}</h1>
        </header>

        <div
          className="mt-2 rounded-[22px] p-[18px] text-white"
          style={{ background: PRESALE_GRADIENT, boxShadow: '0 8px 26px rgba(63,168,198,.28)' }}
        >
          <div className="flex items-center gap-[13px]">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-white/90 shadow-[0_2px_8px_rgba(0,0,0,.1)]">
              <IncptIcon size={72} className="translate-x-[2px]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[16px] font-bold tracking-[-0.3px]">{t('presale.coinName')}</div>
              <div className="mt-px text-[12px] opacity-85">{t('presale.subtitle')}</div>
            </div>
            <div className="text-right">
              <div className="text-[22px] font-bold tracking-[-0.6px]">
                {formatPrice(round.price)}
              </div>
              <div className="text-[11px] opacity-85">{t('presale.perToken')}</div>
            </div>
          </div>

          <div className="mt-3.5 flex gap-2">
            <RoundStat
              label={t('presale.marketPrice')}
              value={`$${formatPrice(round.marketPrice)}`}
            />
            <RoundStat label={t('presale.upside')} value={`${discountPercent(round)}%`} />
            <RoundStat label={t('presale.minimum')} value={fmtWhole(round.minAmount)} />
          </div>
        </div>

        <div className="px-1 pb-1 pt-[18px] text-center">
          <div className="mb-3 text-[13px] text-muted">
            {t('presale.payWith', {
              amount: loading ? '—' : `${formatUsd(usdtBalance)} USDT`,
            })}
          </div>

          <div className="relative">
            <div
              className="inline-block whitespace-nowrap font-extrabold leading-[1.2] tracking-[-2px] tabular-nums text-ink"
              style={{ fontSize: amountFontSize(raw.length) }}
            >
              ${raw ? Number(raw).toLocaleString('en-US') : '0'}
            </div>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              aria-label={t('presale.sheetAmount')}
              value={raw}
              onChange={(e) => setRaw(e.target.value.replace(/\D/g, '').slice(0, 10))}
              className="absolute inset-0 w-full border-none bg-transparent text-center text-[52px] opacity-0 outline-none"
            />
          </div>

          <div className="mt-2 text-[14px] text-muted">
            {t('presale.youGet', { amount: fmtTokens(tokens) })}
          </div>
        </div>

        <div className="mt-3.5 flex gap-2">
          {quick.map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                selection();
                setRaw(String(v));
              }}
              className="h-9 flex-1 rounded-xl bg-white/70 text-[13px] font-semibold text-muted shadow-[0_0_0_0.5px_rgba(0,0,0,.05)] active:bg-white"
            >
              {compact(v)}
            </button>
          ))}
        </div>

        <div className="mt-3.5 rounded-[20px] bg-white px-4 shadow-card">
          <TermRow label={t('presale.rowPrice')} value={`${formatPrice(round.price)} USDT`} />
          <TermRow label={t('presale.rowMin')} value={fmtWhole(round.minAmount)} />
          <TermRow
            label={t('presale.rowGet')}
            value={tokens ? `${fmtTokens(tokens)} ${round.symbol}` : '—'}
            last
          />
        </div>

        {hint && (
          <p className="px-2 pt-3 text-center text-[12px] font-semibold leading-[1.5] text-negative">
            {hint}
          </p>
        )}

        <button
          type="button"
          disabled={!canSubmit}
          onClick={() => {
            impact('medium');
            setConfirming(true);
          }}
          className="mt-5 h-14 w-full rounded-[18px] text-[16px] font-semibold text-white transition-opacity disabled:opacity-40"
          style={{ background: PRESALE_GRADIENT }}
        >
          {t('presale.buy')}
        </button>

        {decimalIsPositive(String(balance)) && (
          <div className="mt-7 flex items-center justify-between rounded-[20px] bg-white px-4 py-3.5 shadow-card">
            <span className="text-[13px] text-muted">{t('presale.holdings')}</span>
            <span className="text-[15px] font-medium text-ink">
              {formatDecimalDisplay(String(balance))} {round.symbol}
            </span>
          </div>
        )}

        <div className="mt-6 px-2 text-center">
          <p className="text-[12px] leading-[1.5] text-muted">{t('presale.phaseNote')}</p>
          <p className="mt-2.5 text-[12px] leading-[1.5]">
            <button
              type="button"
              className="font-medium text-[#377dff] underline"
              onClick={() => openPresaleLegal(PRESALE_AGREEMENT_URL)}
            >
              {t('presale.linkAgreement')}
            </button>
            <span className="text-muted"> • </span>
            <button
              type="button"
              className="font-medium text-[#377dff] underline"
              onClick={() => openPresaleLegal(PRESALE_RISK_URL)}
            >
              {t('presale.linkRisk')}
            </button>
            <span className="text-muted"> • </span>
            <button
              type="button"
              className="font-medium text-[#377dff] underline"
              onClick={() => openPresaleLegal(PRESALE_PRIVACY_URL)}
            >
              {t('presale.linkPrivacy')}
            </button>
          </p>
        </div>

        <h2 className="mt-7 text-[16px] font-medium text-ink">{t('presale.history')}</h2>
        {historyRows.length === 0 ? (
          <div className="mt-3 rounded-[20px] bg-white p-5 text-center text-[13px] text-muted shadow-card">
            {loading ? t('common.loading') : t('presale.historyEmpty')}
          </div>
        ) : (
          <div className="mt-3 flex flex-col gap-2.5">
            {historyRows.map((tx, i) => (
              <TransactionRow key={`presale-tx-${tx.sortTs}-${i}`} tx={tx} light />
            ))}
          </div>
        )}
      </div>

      <PresaleConfirmSheet
        open={confirming}
        amount={amount}
        round={round}
        onClose={() => setConfirming(false)}
        onDone={() => {
          setConfirming(false);
          setRaw('');
        }}
      />
    </div>
  );
}

function RoundStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex-1 rounded-[13px] bg-white/[0.16] px-[11px] py-[9px]">
      <div className="min-h-[26px] text-[10.5px] leading-[1.25] opacity-85">{label}</div>
      <div className="mt-px text-[14px] font-bold">{value}</div>
    </div>
  );
}

function TermRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      className={`flex items-center justify-between py-[11px] text-[13px] ${
        last ? '' : 'border-b border-black/[0.05]'
      }`}
    >
      <span className="text-muted">{label}</span>
      <span className="font-medium tabular-nums text-ink">{value}</span>
    </div>
  );
}
